import React from "react";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import OtpInput from "./forms/OtpInput";
import { auth } from "../services/api";

type SejamSyncModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSynced: () => Promise<unknown>;
};

export const SejamSyncModal: React.FC<SejamSyncModalProps> = ({
  isOpen,
  onClose,
  onSynced,
}) => {
  const toast = useToast();
  const [otp, setOtp] = React.useState("");
  const [otpError, setOtpError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const modalBg = useColorModeValue("white", "dark.50");
  const textColor = useColorModeValue("gray.600", "gray.300");

  const resetState = React.useCallback(() => {
    setOtp("");
    setOtpError("");
    setIsLoading(false);
  }, []);

  const handleClose = React.useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleSubmit = React.useCallback(async () => {
    if (otp.length !== 5) {
      setOtpError("کد تایید باید 5 رقم باشد");
      return;
    }

    try {
      setIsLoading(true);
      setOtpError("");
      await auth.verifySyncOtp(otp);
      await onSynced();
      toast({
        title: "همگام‌سازی انجام شد",
        description: "اطلاعات سجام شما با موفقیت بروزرسانی شد",
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      handleClose();
    } catch (error: any) {
      setOtpError("کد تایید نامعتبر است");
      setOtp("");
      toast({
        title: "خطا در تایید کد",
        description:
          error.response?.data?.message || "کد تایید سجام نامعتبر است",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [handleClose, onSynced, otp, toast]);

  React.useEffect(() => {
    if (otp.length === 5 && isOpen) {
      handleSubmit();
    }
  }, [handleSubmit, isOpen, otp]);

  React.useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg} borderRadius="xl" mx={4}>
        <ModalCloseButton left={4} right="auto" aria-label="بستن" />
        <ModalBody p={8}>
          <VStack spacing={5} align="center" minH="260px" justify="center">
            <VStack spacing={2}>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color={textColor}
                textAlign="center"
              >
                کد تایید سجام را وارد کنید
              </Text>
              <Text fontSize="sm" color={textColor} textAlign="center">
                کد 5 رقمی ارسال شده به شماره موبایل ثبت‌شده در سجام را وارد
                کنید.
              </Text>
            </VStack>

            <Box w="full">
              <OtpInput
                value={otp}
                onChange={setOtp}
                isDisabled={isLoading}
                isInvalid={!!otpError}
                errorMessage={otpError}
                label=""
              />
            </Box>

            <Button
              size="lg"
              width="100%"
              h="55px"
              borderRadius="lg"
              isLoading={isLoading}
              loadingText="در حال بررسی..."
              onClick={handleSubmit}
              isDisabled={otp.length !== 5}
            >
              تایید و همگام‌سازی
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
